/* eslint-disable react/jsx-props-no-spreading */
import AddIcon from '@mui/icons-material/Add';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EditIcon from '@mui/icons-material/Edit';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import {
  Button,
  Chip,
  CircularProgress,
  Grid,
  IconButton,
  Menu,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker';
import { endOfDay, isAfter, startOfDay } from 'date-fns';
import { capitalize } from 'lodash';
import { enqueueSnackbar } from 'notistack';
import { useCallback, useEffect, useState } from 'react';
import { Link, Navigate, useLocation, useNavigate, useParams } from 'react-router-dom';

import { listInvoicesApi, voidInvoiceApi } from 'api/invoiceGroup';

import useInfoDialog from 'common/hooks/useInfoDialog';

import { formatApiDate, isInvoiceLocked } from '../utils/invoiceEditHelpers';
import EditInvoiceGroupContactDialog from './EditInvoiceGroupContactDialog';
import InvoiceDetailsViewer from './InvoiceDetailsViewer';

const ROWS_PER_PAGE_OPTIONS = [25, 50, 100];

const InvoiceGroupInvoices = () => {
  const { invoiceGroupId } = useParams();
  const { pathname, state } = useLocation();
  const navigate = useNavigate();
  // InvoiceCreate and InvoiceEdit hand back the id of the invoice they just wrote so we can pop
  // its PDF open on arrival. Held here rather than read from router state each render, and
  // cleared once the viewer reports it fired, so a dialog the user dismissed doesn't reopen
  // when its row remounts (sorting, paging, the refresh after a void). Also stripped from
  // history below so browser back/forward doesn't replay it.
  const [autoOpenInvoiceId, setAutoOpenInvoiceId] = useState(state?.openInvoiceId);
  // The group's name and billTo only exist in router state, handed down by the invoice group
  // list (there's no query to fetch a single group). A refresh or a typed/bookmarked URL drops
  // that state, so send the user back to the group list to pick the group again rather than
  // rendering a nameless header and passing an undefined billTo on to New Invoice.
  const hasGroupContext = Boolean(state?.invoiceGroupName);

  const [invoices, setInvoices] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);

  const [sortOrder, setSortOrder] = useState('DESC');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  // Draft values reflect what the user is currently typing/picking; they only take effect
  // once "Search" is clicked, rather than firing a request on every keystroke.
  const [invoiceIdInput, setInvoiceIdInput] = useState('');
  const [startDateInput, setStartDateInput] = useState(null);
  const [endDateInput, setEndDateInput] = useState(null);

  const [appliedFilters, setAppliedFilters] = useState({
    invoiceId: undefined,
    startDate: null,
    endDate: null,
  });

  const [menuAnchor, setMenuAnchor] = useState(null);
  const [menuInvoice, setMenuInvoice] = useState(null);
  const [isContactDialogOpen, setIsContactDialogOpen] = useState(false);

  const { InfoDialog, showInfoDialog } = useInfoDialog();

  const isDateRangeInvalid = Boolean(
    startDateInput && endDateInput && isAfter(startDateInput, endDateInput)
  );

  useEffect(() => {
    document.title = 'Invoices - SmartOS';
  }, []);

  useEffect(() => {
    if (!state?.openInvoiceId) return;
    const { openInvoiceId, ...rest } = state;
    navigate(pathname, { replace: true, state: rest });
  }, [navigate, pathname, state]);

  const clearAutoOpen = useCallback(() => setAutoOpenInvoiceId(undefined), []);

  const fetchInvoices = async () => {
    setIsLoading(true);
    setIsError(false);

    try {
      const response = await listInvoicesApi({
        invoiceGroupId,
        invoiceId: appliedFilters.invoiceId,
        startDate: appliedFilters.startDate
          ? startOfDay(appliedFilters.startDate).toISOString()
          : undefined,
        endDate: appliedFilters.endDate
          ? endOfDay(appliedFilters.endDate).toISOString()
          : undefined,
        sortOrder,
        limit: rowsPerPage,
        offset: page * rowsPerPage,
      });
      setInvoices(response?.results ?? []);
      setTotalCount(response?.count ?? 0);
    } catch (error) {
      setIsError(true);
      setInvoices([]);
      setTotalCount(0);
      enqueueSnackbar('Failed to load invoices', { variant: 'error' });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    // Skip the request when we're about to redirect away for missing group context.
    if (!hasGroupContext) return;
    fetchInvoices();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasGroupContext, invoiceGroupId, sortOrder, page, rowsPerPage, appliedFilters]);

  const closeMenu = () => {
    setMenuAnchor(null);
    setMenuInvoice(null);
  };

  const handleVoidInvoice = (invoice) => {
    closeMenu();
    showInfoDialog({
      title: 'Void Invoice',
      message: `Are you sure you want to void invoice #${invoice.invoiceId}? This cannot be undone.`,
      cancelable: true,
      confirmButtonText: 'Yes, Void Invoice',
      isWarning: true,
      callback: async () => {
        try {
          await voidInvoiceApi({ invoiceId: invoice.invoiceId });
          enqueueSnackbar(`Invoice #${invoice.invoiceId} voided`, { variant: 'success' });
          await fetchInvoices();
        } catch (error) {
          enqueueSnackbar(`Failed to void invoice #${invoice.invoiceId}`, { variant: 'error' });
        }
      },
    });
  };

  // The group lives only in router state, so write the saved contact back there. New Invoice and
  // Edit Invoice read billTo from it, and replacing the entry keeps browser back from restoring
  // the old contact.
  const handleContactSaved = (updatedGroup) => {
    setIsContactDialogOpen(false);
    navigate(pathname, {
      replace: true,
      state: {
        ...state,
        invoiceGroupName: updatedGroup.invoiceGroupName,
        billTo: updatedGroup.billTo,
      },
    });
  };

  const handleSortByDate = () => {
    setSortOrder((current) => (current === 'ASC' ? 'DESC' : 'ASC'));
    setPage(0);
  };

  const handleInvoiceIdInputChange = (event) => {
    setInvoiceIdInput(event.target.value.replace(/\D/g, ''));
  };

  const handleSearch = () => {
    if (isDateRangeInvalid) {
      return;
    }

    setAppliedFilters({
      invoiceId: invoiceIdInput ? Number(invoiceIdInput) : undefined,
      startDate: startDateInput,
      endDate: endDateInput,
    });
    setPage(0);
  };

  const handleInvoiceIdKeyDown = (event) => {
    if (event.key === 'Enter') {
      handleSearch();
    }
  };

  if (!hasGroupContext) {
    return <Navigate to='/adminCenter/invoiceGroups' replace />;
  }

  return (
    <Grid container direction='column' sx={{ px: 3 }} gap={2}>
      <Grid item container justifyContent='space-between' alignItems='center' rowGap={2}>
        <Grid item container alignItems='center' gap={1} sx={{ width: 'auto' }}>
          <IconButton component={Link} to='/adminCenter/invoiceGroups' size='small'>
            <ArrowBackIcon fontSize='small' />
          </IconButton>
          <Typography component='h1' variant='h5'>
            {state.invoiceGroupName}
          </Typography>
          <Button
            size='small'
            variant='outlined'
            startIcon={<EditIcon />}
            onClick={() => setIsContactDialogOpen(true)}
            sx={{ ml: 1, whiteSpace: 'nowrap' }}
          >
            Edit Contact
          </Button>
        </Grid>
        <Grid item>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems='center'>
            <TextField
              size='small'
              label='Invoice Number'
              placeholder='e.g. 201'
              value={invoiceIdInput}
              onChange={handleInvoiceIdInputChange}
              onKeyDown={handleInvoiceIdKeyDown}
              sx={{ minWidth: 160 }}
            />
            <DesktopDatePicker
              label='Start Date'
              inputFormat='MM/dd/yyyy'
              value={startDateInput}
              maxDate={endDateInput ?? undefined}
              onChange={setStartDateInput}
              renderInput={(params) => (
                <TextField {...params} size='small' sx={{ minWidth: 160 }} />
              )}
            />
            <DesktopDatePicker
              label='End Date'
              inputFormat='MM/dd/yyyy'
              value={endDateInput}
              minDate={startDateInput ?? undefined}
              onChange={setEndDateInput}
              renderInput={(params) => (
                <TextField {...params} size='small' sx={{ minWidth: 160 }} />
              )}
            />
            <Button variant='contained' onClick={handleSearch} disabled={isDateRangeInvalid}>
              Search
            </Button>
            <Button
              variant='outlined'
              startIcon={<AddIcon />}
              component={Link}
              to={`/adminCenter/invoiceGroups/${invoiceGroupId}/invoices/new`}
              state={{
                fromInvoiceList: true,
                invoiceGroupName: state?.invoiceGroupName,
                billTo: state?.billTo,
              }}
              sx={{ whiteSpace: 'nowrap' }}
            >
              New Invoice
            </Button>
          </Stack>
        </Grid>
      </Grid>

      {isDateRangeInvalid && (
        <Grid item container justifyContent='flex-end'>
          <Typography variant='body2' color='error'>
            End date cannot be before start date.
          </Typography>
        </Grid>
      )}

      {isLoading ? (
        <Grid item container justifyContent='center' sx={{ py: 6 }}>
          <CircularProgress />
        </Grid>
      ) : (
        <Grid item>
          <TableContainer component={Paper}>
            <Table aria-label='invoices table'>
              <TableHead>
                <TableRow>
                  <TableCell
                    sx={{ width: 150 }}
                    sortDirection={sortOrder === 'ASC' ? 'asc' : 'desc'}
                  >
                    <TableSortLabel
                      active
                      direction={sortOrder === 'ASC' ? 'asc' : 'desc'}
                      onClick={handleSortByDate}
                    >
                      Invoice Date
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sx={{ width: 150 }}>Invoice Number</TableCell>
                  <TableCell align='right'>Status</TableCell>
                  <TableCell align='right' sx={{ width: 60 }} />
                </TableRow>
              </TableHead>
              <TableBody>
                {invoices.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} align='center'>
                      {isError ? 'Unable to load invoices.' : 'No invoices found.'}
                    </TableCell>
                  </TableRow>
                ) : (
                  invoices.map((invoice) => {
                    return (
                      <TableRow key={invoice.invoiceId} hover>
                        <TableCell sx={{ width: 150 }}>
                          {formatApiDate(invoice.invoiceDate)}
                        </TableCell>
                        <TableCell sx={{ width: 150 }}>
                          <InvoiceDetailsViewer
                            invoiceId={invoice.invoiceId}
                            defaultOpen={invoice.invoiceId === autoOpenInvoiceId}
                            onAutoOpened={clearAutoOpen}
                          />
                        </TableCell>
                        <TableCell align='right'>
                          <Chip size='small' label={capitalize(invoice.invoiceStatus)} />
                        </TableCell>
                        <TableCell align='right' sx={{ width: 60 }}>
                          <IconButton
                            size='small'
                            aria-label='Invoice actions'
                            onClick={(event) => {
                              setMenuAnchor(event.currentTarget);
                              setMenuInvoice(invoice);
                            }}
                          >
                            <MoreVertIcon fontSize='small' />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={closeMenu}>
            {menuInvoice &&
              (isInvoiceLocked(menuInvoice.invoiceStatus) ? (
                <Tooltip title='Paid or void invoices cannot be edited' placement='left'>
                  <span>
                    <MenuItem disabled>Edit Invoice</MenuItem>
                  </span>
                </Tooltip>
              ) : (
                <MenuItem
                  component={Link}
                  to={`/adminCenter/invoiceGroups/${invoiceGroupId}/invoices/${menuInvoice.invoiceId}/edit`}
                  state={{
                    fromInvoiceList: true,
                    invoiceGroupName: state.invoiceGroupName,
                    billTo: state.billTo,
                  }}
                  onClick={closeMenu}
                >
                  Edit Invoice
                </MenuItem>
              ))}
            {menuInvoice &&
              (isInvoiceLocked(menuInvoice.invoiceStatus) ? (
                <Tooltip title='Paid or void invoices cannot be voided' placement='left'>
                  <span>
                    <MenuItem disabled sx={{ color: 'error.main' }}>
                      Void Invoice
                    </MenuItem>
                  </span>
                </Tooltip>
              ) : (
                <MenuItem
                  sx={{ color: 'error.main' }}
                  onClick={() => handleVoidInvoice(menuInvoice)}
                >
                  Void Invoice
                </MenuItem>
              ))}
          </Menu>

          <InfoDialog />

          <EditInvoiceGroupContactDialog
            open={isContactDialogOpen}
            onClose={() => setIsContactDialogOpen(false)}
            onSaved={handleContactSaved}
            invoiceGroupId={invoiceGroupId}
            billTo={state.billTo}
          />

          <Grid
            container
            justifyContent='space-between'
            alignItems='center'
            sx={{ mt: 1, flexWrap: 'wrap' }}
          >
            <Typography variant='body2' color='text.secondary'>
              {`Showing ${invoices.length} of ${totalCount} invoices`}
            </Typography>
            <TablePagination
              component='div'
              count={totalCount}
              page={page}
              onPageChange={(event, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
              onRowsPerPageChange={(event) => {
                setRowsPerPage(parseInt(event.target.value, 10));
                setPage(0);
              }}
            />
          </Grid>
        </Grid>
      )}
    </Grid>
  );
};

export default InvoiceGroupInvoices;
